import React, { useState, useEffect } from "react";
import { useCreateGraphicsItem, useUpdateGraphicsItem } from "./hooks";
import { GraphicsItem } from "./api";
import { useT } from "../../i18n";

interface GraphicsModalProps {
  isOpen: boolean;
  onClose: () => void;
  editItem?: GraphicsItem | null;
}

const GraphicsModal: React.FC<GraphicsModalProps> = ({
  isOpen,
  onClose,
  editItem,
}) => {
  const t = useT();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Logos & Brand Marks",
    isActive: true,
    sortOrder: 0,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);

  const createMutation = useCreateGraphicsItem();
  const updateMutation = useUpdateGraphicsItem();

  useEffect(() => {
    if (editItem) {
      setFormData({
        title: editItem.title,
        description: editItem.description,
        category: editItem.category,
        isActive: editItem.isActive,
        sortOrder: editItem.sortOrder,
      });
    } else {
      setFormData({
        title: "",
        description: "",
        category: "Logos & Brand Marks",
        isActive: true,
        sortOrder: 0,
      });
    }
    setSelectedFile(null);
    setFilePreview(null);
  }, [editItem, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);

      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editItem) {
        await updateMutation.mutateAsync({
          id: editItem.id,
          data: formData,
          file: selectedFile || undefined,
        });
      } else {
        await createMutation.mutateAsync({
          data: formData,
          file: selectedFile || undefined,
        });
      }
      onClose();
    } catch (error) {
      console.error("Error saving graphics item:", error);
    }
  };

  const categories = [
    { value: "Logos & Brand Marks", label: "Logos & Brand Marks" },
    { value: "Marketing Banners", label: "Marketing Banners" },
    { value: "Print Materials", label: "Print Materials" },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {editItem
              ? t("graphics.modal.editTitle")
              : t("graphics.modal.createTitle")}
          </h2>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            className="text-gray-400 hover:text-gray-600"
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("graphics.modal.titleLabel")}
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t("graphics.modal.titlePlaceholder")}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("graphics.modal.descriptionLabel")}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t("graphics.modal.descriptionPlaceholder")}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("graphics.modal.categoryLabel")}
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("graphics.modal.fileLabel")} {!editItem && "*"}
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept="image/*,.pdf,.ai,.eps,.svg,.psd"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t("graphics.modal.supportedFormats")}
            </p>

            {/* File Preview */}
            {filePreview && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("graphics.modal.previewLabel")}
                </label>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <img
                    src={filePreview}
                    alt={t("common.preview")}
                    className="max-w-full max-h-48 object-contain mx-auto"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Status and Sort Order */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {t("graphics.modal.activeLabel")}
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("graphics.modal.sortOrderLabel")}
              </label>
              <input
                type="number"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {editItem ? t("common.update") : t("common.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GraphicsModal;
